import React, { useState } from "react";
import { Form, Input, Button, Card, Progress, Typography } from "antd";
import { useDispatch } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { changePassword } from "../../store/authSlice";

const { Title } = Typography;

const ChangePasswordPage = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordError, setPasswordError] = useState("");
  const [passwordValid, setPasswordValid] = useState(false);

  const calculateStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength += 20;
    if (/[A-Z]/.test(password)) strength += 20;
    if (/[a-z]/.test(password)) strength += 20;
    if (/\d/.test(password)) strength += 20;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 20;
    setPasswordValid(strength === 100);
    return strength;
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    const strength = calculateStrength(newPassword);
    setPasswordStrength(strength);

    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters long.");
    } else if (newPassword === form.getFieldValue("currentPassword")) {
      setPasswordError(
        "New password cannot be the same as the current password."
      );
    } else if (!/[A-Z]/.test(newPassword)) {
      setPasswordError("Password must include at least one uppercase letter.");
    } else if (!/[a-z]/.test(newPassword)) {
      setPasswordError("Password must include at least one lowercase letter.");
    } else if (!/\d/.test(newPassword)) {
      setPasswordError("Password must include at least one digit.");
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      setPasswordError("Password must include at least one special character.");
    } else {
      setPasswordError("");
    }
  };

  const onFinish = async (values) => {
    try {
      await dispatch(
        changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        })
      ).unwrap();

      toast.success("Password changed successfully!");
      form.resetFields();
      setPasswordStrength(0);
      setPasswordError("");
      setPasswordValid(false);
    } catch (error) {
      toast.error(error || "Failed to change password.");
    }
  };

  const validateNewPassword = (rule, value, callback) => {
    const { currentPassword } = form.getFieldsValue();
    if (value === currentPassword) {
      callback("New password cannot be the same as the current password!");
    } else {
      callback();
    }
  };

  return (
    <div style={{ maxWidth: 400, margin: "50px auto" }}>
      <Card title="🔒 Change Password">
        <Form layout="vertical" onFinish={onFinish} form={form}>
          <Form.Item
            label="Current Password"
            name="currentPassword"
            rules={[
              { required: true, message: "Please enter your current password" },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: "Please enter your new password" },
              {
                min: 6,
                max: 100,
                message: "Password must be between 6 and 100 characters",
              },
              {
                pattern:
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,100}$/,
                message:
                  "Password must include uppercase, lowercase, digit, and special character",
              },
              validateNewPassword,
            ]}
          >
            <Input.Password onChange={handlePasswordChange} />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match!"));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item>
            <Progress
              percent={passwordStrength}
              status={passwordStrength < 50 ? "exception" : "success"}
              showInfo={false}
            />
            {passwordValid && (
              <span
                style={{ color: "green", fontSize: "12px", marginTop: "5px" }}
              >
                Password is strong enough
              </span>
            )}
            {passwordError && (
              <span style={{ color: "red", fontSize: "12px" }}>
                {passwordError}
              </span>
            )}
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Change Password
            </Button>
          </Form.Item>
        </Form>
      </Card>
      <ToastContainer position="top-center" />
    </div>
  );
};

export default ChangePasswordPage;
